<script lang="js">
import CTA from "@/components/CTA.vue";
import Swal from "sweetalert2";
import axios from "axios";

export default {
  name: "JobDetailView",
  components: { CTA },
  data() {
    return {
      logger: null,
      job: null,
      loading: true,
      submitting: false,
      formData: {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        city: "",
        experienceLevel: "",
        preferredStartDate: "",
        message: "",
      },
      resumeFile: null,
      coverLetterFile: null,
      experienceLevels: ["0-2 years", "3-5 years", "5+ years"],
    };
  },
  created() {
    this.logger = this.$createLogger("JobDetailView");
  },
  mounted() {
    this.fetchJob();
  },
  methods: {
    async fetchJob() {
      try {
        this.loading = true;
        const jobId = this.$route.params.jobId;
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/jobs/${jobId}`
        );
        if (response.data.success) {
          this.job = response.data.data;
        } else {
          this.$router.push("/careers");
        }
      } catch (error) {
        this.logger.error("Error fetching job:", error);
        this.$router.push("/careers");
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-NG", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    getTypeColor(type) {
      const colors = {
        "Full-Time": "primary",
        "Part-Time": "info",
        Contract: "warning",
        Temporary: "secondary",
        Internship: "success",
      };
      return colors[type] || "secondary";
    },
    handleResumeUpload(event) {
      const file = event.target.files[0];
      if (file) {
        if (file.type !== "application/pdf") {
          Swal.fire("Error", "Please upload a PDF file for your resume.", "error");
          event.target.value = "";
          return;
        }
        if (file.size > 2 * 1024 * 1024) {
          Swal.fire("Error", "Resume file must be under 2MB.", "error");
          event.target.value = "";
          return;
        }
        this.resumeFile = file;
      }
    },
    handleCoverLetterUpload(event) {
      const file = event.target.files[0];
      if (file) {
        if (file.type !== "application/pdf") {
          Swal.fire("Error", "Please upload a PDF file for your cover letter.", "error");
          event.target.value = "";
          return;
        }
        if (file.size > 2 * 1024 * 1024) {
          Swal.fire("Error", "Cover letter file must be under 2MB.", "error");
          event.target.value = "";
          return;
        }
        this.coverLetterFile = file;
      }
    },
    async submitApplication() {
      // Validate required fields
      if (
        !this.formData.firstName ||
        !this.formData.lastName ||
        !this.formData.email ||
        !this.formData.phone ||
        !this.formData.experienceLevel ||
        !this.resumeFile
      ) {
        Swal.fire(
          "Missing Fields",
          "Please fill in all required fields and upload your resume.",
          "warning"
        );
        return;
      }

      try {
        this.submitting = true;

        const data = new FormData();
        data.append("jobId", this.job.jobId);
        data.append("firstName", this.formData.firstName);
        data.append("lastName", this.formData.lastName);
        data.append("email", this.formData.email);
        data.append("phone", this.formData.phone);
        data.append("city", this.formData.city);
        data.append("experienceLevel", this.formData.experienceLevel);
        if (this.formData.preferredStartDate) {
          data.append("preferredStartDate", this.formData.preferredStartDate);
        }
        if (this.formData.message) {
          data.append("message", this.formData.message);
        }
        data.append("resume", this.resumeFile);
        if (this.coverLetterFile) {
          data.append("coverLetter", this.coverLetterFile);
        }

        const response = await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/job-applications`,
          data,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        if (response.data.success) {
          Swal.fire({
            icon: "success",
            title: "Application Submitted!",
            html: `
              <p>Thank you for applying for the <strong>${response.data.data.jobTitle}</strong> position.</p>
              <p>Your Application ID: <strong>${response.data.data.applicationId}</strong></p>
              <p class="text-muted small">A confirmation email has been sent to your email address.</p>
            `,
            confirmButtonColor: "#003366",
          });
          this.resetForm();
        }
      } catch (error) {
        this.logger.error("Error submitting application:", error);
        const message =
          error.response?.data?.message ||
          "Something went wrong. Please try again.";
        Swal.fire("Error", message, "error");
      } finally {
        this.submitting = false;
      }
    },
    resetForm() {
      this.formData = {
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        city: "",
        experienceLevel: "",
        preferredStartDate: "",
        message: "",
      };
      this.resumeFile = null;
      this.coverLetterFile = null;
      // Reset file inputs
      const resumeInput = this.$refs.resumeInput;
      const coverLetterInput = this.$refs.coverLetterInput;
      if (resumeInput) resumeInput.value = "";
      if (coverLetterInput) coverLetterInput.value = "";
    },
    scrollToForm() {
      const formEl = document.getElementById("application-form");
      if (formEl) {
        formEl.scrollIntoView({ behavior: "smooth" });
      }
    },
  },
};
</script>

<template>
  <div class="job-detail-page">
    <!-- Hero Banner -->
    <div class="bg-image-overlay">
      <div class="overlay"></div>
      <div class="content">
        <router-link to="/careers" class="btn btn-outline-light btn-sm mb-3">
          <i class="bi bi-arrow-left me-1"></i> All Positions
        </router-link>
        <h1 v-scrollanimation.once="'fadeInUp'" v-if="job">{{ job.title }}</h1>
        <div v-if="job" class="mt-3">
          <span
            class="badge rounded-pill me-2"
            :class="`bg-${getTypeColor(job.employmentType)}`"
          >
            {{ job.employmentType }}
          </span>
          <span class="badge bg-light text-dark me-2" v-if="job.department">
            <i class="bi bi-building me-1"></i>{{ job.department }}
          </span>
          <span class="badge bg-light text-dark">
            <i class="bi bi-geo-alt me-1"></i>{{ job.location }}
          </span>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="container text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Job Content -->
    <div v-else-if="job" class="container my-5">
      <div class="row">
        <!-- Left Column - Job Details -->
        <div class="col-lg-7 mb-4 mb-lg-0">
          <div class="pe-lg-4">
            <!-- Job Info -->
            <div class="mb-4">
              <div class="d-flex align-items-center text-muted mb-3">
                <small>
                  <i class="bi bi-calendar me-1"></i> Posted
                  {{ formatDate(job.createdAt) }}
                </small>
                <small class="ms-3">
                  <i class="bi bi-hash me-1"></i>{{ job.jobId }}
                </small>
              </div>
            </div>

            <!-- Description -->
            <div class="mb-4">
              <h5 class="fw-bold">About the Role</h5>
              <p class="text-muted" style="white-space: pre-line">
                {{ job.description }}
              </p>
            </div>

            <!-- Responsibilities -->
            <div
              class="mb-4"
              v-if="job.responsibilities && job.responsibilities.length > 0"
            >
              <h5 class="fw-bold">Key Responsibilities</h5>
              <ul class="list-unstyled">
                <li
                  v-for="(item, index) in job.responsibilities"
                  :key="index"
                  class="mb-2"
                >
                  <i class="bi bi-check-circle-fill text-success me-2"></i>
                  {{ item }}
                </li>
              </ul>
            </div>

            <!-- Requirements -->
            <div
              class="mb-4"
              v-if="job.requirements && job.requirements.length > 0"
            >
              <h5 class="fw-bold">Requirements</h5>
              <ul class="list-unstyled">
                <li
                  v-for="(item, index) in job.requirements"
                  :key="index"
                  class="mb-2"
                >
                  <i class="bi bi-dot fs-4 text-primary me-1"></i>
                  {{ item }}
                </li>
              </ul>
            </div>

            <!-- Tags -->
            <div class="mb-4" v-if="job.tags && job.tags.length > 0">
              <h5 class="fw-bold">Skills & Tags</h5>
              <span
                v-for="tag in job.tags"
                :key="tag"
                class="badge bg-light text-dark me-2 mb-2 px-3 py-2"
              >
                {{ tag }}
              </span>
            </div>

            <!-- Apply CTA for mobile -->
            <div class="d-lg-none mb-4">
              <button class="btn btn-primary w-100 py-2" @click="scrollToForm">
                <i class="bi bi-send me-1"></i> Apply for this Position
              </button>
            </div>
          </div>
        </div>

        <!-- Right Column - Application Form -->
        <div class="col-lg-5" id="application-form">
          <div class="card border-0 shadow-sm sticky-form">
            <div class="card-body p-4">
              <h5 class="fw-bold mb-1">Apply for this Position</h5>
              <p class="text-muted small mb-4">
                Fields marked with * are required
              </p>

              <form @submit.prevent="submitApplication">
                <div class="row">
                  <div class="col-6 mb-3">
                    <label class="form-label small fw-semibold"
                      >First Name *</label
                    >
                    <input
                      type="text"
                      class="form-control"
                      v-model="formData.firstName"
                      required
                      placeholder="John"
                    />
                  </div>
                  <div class="col-6 mb-3">
                    <label class="form-label small fw-semibold"
                      >Last Name *</label
                    >
                    <input
                      type="text"
                      class="form-control"
                      v-model="formData.lastName"
                      required
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div class="mb-3">
                  <label class="form-label small fw-semibold"
                    >Email Address *</label
                  >
                  <input
                    type="email"
                    class="form-control"
                    v-model="formData.email"
                    required
                    placeholder="john.doe@example.com"
                  />
                </div>

                <div class="mb-3">
                  <label class="form-label small fw-semibold"
                    >Phone Number *</label
                  >
                  <input
                    type="tel"
                    class="form-control"
                    v-model="formData.phone"
                    required
                    placeholder="+234..."
                  />
                </div>

                <div class="row">
                  <div class="col-6 mb-3">
                    <label class="form-label small fw-semibold">City</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="formData.city"
                      placeholder="Lagos"
                    />
                  </div>
                  <div class="col-6 mb-3">
                    <label class="form-label small fw-semibold"
                      >Experience Level *</label
                    >
                    <select
                      class="form-select"
                      v-model="formData.experienceLevel"
                      required
                    >
                      <option value="" disabled>Select...</option>
                      <option
                        v-for="level in experienceLevels"
                        :key="level"
                        :value="level"
                      >
                        {{ level }}
                      </option>
                    </select>
                  </div>
                </div>

                <div class="mb-3">
                  <label class="form-label small fw-semibold"
                    >Preferred Start Date</label
                  >
                  <input
                    type="date"
                    class="form-control"
                    v-model="formData.preferredStartDate"
                  />
                </div>

                <div class="mb-3">
                  <label class="form-label small fw-semibold">
                    Resume / CV *
                    <span class="text-muted fw-normal">(PDF, max 2MB)</span>
                  </label>
                  <input
                    type="file"
                    class="form-control"
                    accept=".pdf"
                    ref="resumeInput"
                    @change="handleResumeUpload"
                    required
                  />
                </div>

                <div class="mb-3">
                  <label class="form-label small fw-semibold">
                    Cover Letter
                    <span class="text-muted fw-normal"
                      >(PDF, max 2MB, optional)</span
                    >
                  </label>
                  <input
                    type="file"
                    class="form-control"
                    accept=".pdf"
                    ref="coverLetterInput"
                    @change="handleCoverLetterUpload"
                  />
                </div>

                <div class="mb-4">
                  <label class="form-label small fw-semibold"
                    >Additional Message</label
                  >
                  <textarea
                    class="form-control"
                    rows="3"
                    v-model="formData.message"
                    placeholder="Tell us why you'd be a great fit..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  class="btn btn-primary w-100 py-2"
                  :disabled="submitting"
                >
                  <span
                    v-if="submitting"
                    class="spinner-border spinner-border-sm me-2"
                  ></span>
                  {{ submitting ? "Submitting..." : "Submit Application" }}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CTA -->
    <div class="container-fluid bg-light py-3">
      <CTA
        heading="Explore More Opportunities"
        message="Browse all open positions and find the perfect role for your career at First City Polytechnic."
        link="/careers"
        button="View All Positions"
        v-scrollanimation.once="'zoomIn'"
      />
    </div>
  </div>
</template>

<style scoped>
.bg-image-overlay {
  position: relative;
  background-image: linear-gradient(
    135deg,
    #0e1438 0%,
    #1a2a6c 50%,
    #0e1438 100%
  );
  background-size: cover;
  background-position: center;
  height: 40vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(14, 20, 56, 0.7);
  z-index: 1;
}

.content {
  position: relative;
  z-index: 2;
}

.sticky-form {
  position: sticky;
  top: 20px;
  border-radius: 12px;
}

@media (max-width: 991.98px) {
  .sticky-form {
    position: static;
  }
}
</style>
